import React, { useEffect, useState } from "react";
import { Bell, BellOff, Moon, Loader2, Pill, Calendar, ListChecks, CheckSquare, MessageCircle, Sparkles, BarChart2, ChevronDown } from "lucide-react";
import { BottomSheet } from "@/components/BottomSheet";
import { ReminderEditor } from "@/components/ReminderEditor";
import { api } from "@/lib/api";

const CATEGORIES = [
  { key: "events",            label: "Events & meetings", icon: Calendar,      always: true },
  { key: "medications",       label: "Medications",       icon: Pill,          critical: true },
  { key: "chores",            label: "Chores",            icon: ListChecks },
  { key: "todos",             label: "To-dos",            icon: CheckSquare },
  { key: "polls",             label: "Polls",             icon: BarChart2 },
  { key: "chat_mentions",     label: "Chat mentions",     icon: MessageCircle },
  { key: "kinly_suggestions", label: "Kinly suggestions", icon: Sparkles },
];

/**
 * NotificationSettingsSheet — Settings → Notifications.
 *
 * Refreshed UI:
 *   - One big master toggle card with a clear ON/OFF status pill.
 *   - All categories as a single compact list with right-aligned toggles
 *     (no nested channel chip rows). Channels (push / alarm) move into a
 *     collapsible "Advanced" disclosure beneath the master toggle.
 *   - Quiet hours as an inline expandable block.
 *   - Default reminder time at the bottom (rarely changed).
 *
 * Designed to look at home next to iOS Settings → Notifications.
 */
export function NotificationSettingsSheet({ open, onClose }) {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    api.get("/users/me/notification-settings").then(({ data }) => {
      if (!cancelled) setSettings(data);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [open]);

  const persist = async (patch) => {
    setSettings((s) => ({ ...(s || {}), ...patch }));
    setSaving(true);
    try {
      const { data } = await api.patch("/users/me/notification-settings", patch);
      setSettings(data);
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const masterOn = settings?.master_enabled !== false;

  return (
    <BottomSheet open onClose={onClose} title="Notifications" fullHeight>
      <div className="space-y-4 pb-6" data-testid="notification-settings-sheet">
        {!settings ? (
          <div className="kinly-card flex items-center gap-2 text-muted-foreground text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : (
          <>
            {/* Master card */}
            <article className="kinly-card" data-testid="notif-master-card">
              <div className="flex items-center gap-3">
                <div
                  className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 ${
                    masterOn ? "bg-primary/15 text-primary" : "bg-secondary text-muted-foreground"
                  }`}
                >
                  {masterOn ? <Bell className="w-5 h-5" /> : <BellOff className="w-5 h-5" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-display text-[17px] tracking-tight">All notifications</p>
                  <p className="text-[12px] text-muted-foreground mt-0.5">
                    {masterOn ? "Kinly can reach you" : "Silent — nothing will alert you"}
                  </p>
                </div>
                <Switch value={masterOn} onChange={(v) => persist({ master_enabled: v })} testid="notif-master" />
              </div>
            </article>

            {/* Categories — disabled visually when master is off */}
            <article className={`kinly-card ${masterOn ? "" : "opacity-60 pointer-events-none"}`} data-testid="notif-categories-card">
              <p className="pb-3 text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-medium">
                What you hear from
              </p>
              <ul className="divide-y divide-border/60 -mx-1">
                {CATEGORIES.map((c) => {
                  const cat = settings[c.key] || { enabled: true, channels: ["push"] };
                  return (
                    <li key={c.key} className="flex items-center gap-3 py-2.5 px-1" data-testid={`notif-cat-${c.key}`}>
                      <div className="w-9 h-9 rounded-xl bg-secondary/60 text-foreground flex items-center justify-center shrink-0">
                        <c.icon className="w-4 h-4" strokeWidth={1.7} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[14px] font-medium leading-tight">{c.label}</p>
                        {c.critical && (
                          <p className="text-[10px] uppercase tracking-wider text-primary mt-0.5">Recommended</p>
                        )}
                      </div>
                      <Switch
                        value={!!cat.enabled}
                        onChange={(v) => persist({ [c.key]: { ...cat, enabled: v } })}
                        testid={`notif-cat-${c.key}-toggle`}
                      />
                    </li>
                  );
                })}
              </ul>
            </article>

            {/* Advanced disclosure: channels per category. Rarely touched. */}
            <article className="kinly-card" data-testid="notif-advanced-card">
              <button
                type="button"
                onClick={() => setAdvancedOpen((s) => !s)}
                className="w-full flex items-center justify-between tap"
                data-testid="notif-advanced-toggle"
              >
                <div>
                  <p className="text-[14px] font-medium">Channels (advanced)</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">
                    Push, alarm, or both — per category
                  </p>
                </div>
                <ChevronDown
                  className={`w-4 h-4 text-muted-foreground transition-transform ${advancedOpen ? "rotate-180" : ""}`}
                />
              </button>
              {advancedOpen && (
                <ul className="mt-3 divide-y divide-border/60 -mx-1">
                  {CATEGORIES.map((c) => {
                    const cat = settings[c.key] || { enabled: true, channels: ["push"] };
                    return (
                      <li key={c.key} className="flex items-center justify-between py-2.5 px-1">
                        <span className="text-[13px] text-foreground">{c.label}</span>
                        <ChannelChips
                          value={cat.channels || ["push"]}
                          onChange={(channels) => persist({ [c.key]: { ...cat, channels } })}
                          testid={`notif-cat-${c.key}`}
                        />
                      </li>
                    );
                  })}
                </ul>
              )}
            </article>

            {/* Quiet hours */}
            <article className="kinly-card" data-testid="notif-quiet-card">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-secondary/60 text-foreground flex items-center justify-center shrink-0">
                  <Moon className="w-4 h-4" strokeWidth={1.7} />
                </div>
                <div className="flex-1">
                  <p className="text-[14px] font-medium leading-tight">Quiet hours</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">
                    {settings.quiet_hours?.enabled
                      ? `${settings.quiet_hours.start || "22:00"} – ${settings.quiet_hours.end || "07:00"}`
                      : "Off"}
                  </p>
                </div>
                <Switch
                  value={!!settings.quiet_hours?.enabled}
                  onChange={(v) => persist({ quiet_hours: { ...(settings.quiet_hours || {}), enabled: v } })}
                  testid="notif-quiet-toggle"
                />
              </div>
              {settings.quiet_hours?.enabled && (
                <div className="mt-3 space-y-3 pl-12" data-testid="notif-quiet-detail">
                  <div className="grid grid-cols-2 gap-2">
                    <TimeField
                      label="From"
                      value={settings.quiet_hours.start || "22:00"}
                      onChange={(v) => persist({ quiet_hours: { ...settings.quiet_hours, start: v } })}
                      testid="notif-quiet-start"
                    />
                    <TimeField
                      label="Until"
                      value={settings.quiet_hours.end || "07:00"}
                      onChange={(v) => persist({ quiet_hours: { ...settings.quiet_hours, end: v } })}
                      testid="notif-quiet-end"
                    />
                  </div>
                  <label className="flex items-center justify-between text-[13px]">
                    <span>Let medications still ring</span>
                    <Switch
                      value={!!settings.quiet_hours.allow_urgent}
                      onChange={(v) => persist({ quiet_hours: { ...settings.quiet_hours, allow_urgent: v } })}
                      testid="notif-quiet-urgent"
                    />
                  </label>
                </div>
              )}
            </article>

            {/* Default reminder offset */}
            <article className="kinly-card" data-testid="notif-default-card">
              <p className="text-[14px] font-medium">Default reminder</p>
              <p className="text-[11px] text-muted-foreground mt-0.5 mb-3 leading-snug">
                How early Kinly nudges you when a new event doesn&apos;t pick its own time.
              </p>
              <ReminderEditor
                value={settings.default_reminder_minutes || [60]}
                onChange={(v) => persist({ default_reminder_minutes: v })}
                testidPrefix="notif-default-reminder"
              />
            </article>

            {saving && (
              <p className="text-xs text-muted-foreground text-center inline-flex items-center justify-center gap-1.5">
                <Loader2 className="w-3 h-3 animate-spin" /> Saving…
              </p>
            )}
          </>
        )}
      </div>
    </BottomSheet>
  );
}

function Switch({ value, onChange, testid }) {
  return (
    <button
      type="button" role="switch" aria-checked={value}
      data-testid={testid}
      onClick={() => onChange(!value)}
      className={[
        "shrink-0 w-[44px] h-[26px] rounded-full relative transition-colors tap",
        value ? "bg-primary" : "bg-secondary",
      ].join(" ")}
    >
      <span
        className={[
          "absolute top-[2px] w-[22px] h-[22px] rounded-full bg-background shadow-sm transition-transform",
          value ? "translate-x-[20px]" : "translate-x-[2px]",
        ].join(" ")}
      />
    </button>
  );
}

function ChannelChips({ value, onChange, testid }) {
  const toggle = (v) => {
    const next = value.includes(v) ? value.filter((x) => x !== v) : [...value, v];
    onChange(next.length ? next : [v]); // never leave empty
  };
  return (
    <div className="flex gap-1.5">
      {[{ v: "push", label: "Push" }, { v: "alarm", label: "Alarm" }].map((ch) => {
        const on = value.includes(ch.v);
        return (
          <button
            key={ch.v}
            type="button"
            data-testid={`${testid}-channel-${ch.v}`}
            onClick={() => toggle(ch.v)}
            className={[
              "h-7 px-2.5 rounded-full text-[11px] font-medium tap border",
              on ? "bg-foreground text-background border-foreground" : "border-border text-muted-foreground",
            ].join(" ")}
          >{ch.label}</button>
        );
      })}
    </div>
  );
}

function TimeField({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-medium">{label}</span>
      <input
        type="time" value={value} onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="mt-1 w-full bg-card border border-border rounded-xl px-3 py-2 outline-none text-[15px] focus:border-primary"
      />
    </label>
  );
}
